import { useState, useRef, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Crown,
  Mic,
  MicOff,
  Send,
  Bot,
  User,
  Upload,
  Play,
  Square,
  FileAudio,
  Clock,
  FileText,
  Loader2,
  Phone,
  Video,
  Download,
  Trash2,
  Eye,
  Brain,
  CheckCircle2,
  AlertCircle,
  Waves,
  Calendar,
  Mail,
  ListTodo,
  Tractor,
  Sparkles,
  MessageSquare,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssistantePage() {
  const { token, user, currentSociete } = useAuth();
  const [activeTab, setActiveTab] = useState('assistant');
  
  // États Assistant IA
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  
  // États Réunions
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcription, setTranscription] = useState('');
  const [reunions, setReunions] = useState([]);
  const [selectedReunion, setSelectedReunion] = useState(null);
  const [meetingName, setMeetingName] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  
  // États Visio (invitations & analyse)
  const [visioInvitations, setVisioInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [visioRecords, setVisioRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [uploadingVisio, setUploadingVisio] = useState(false);
  const [visioTitle, setVisioTitle] = useState('');
  
  // Refs pour l'enregistrement
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const chunkIntervalRef = useRef(null);

  // Charger les réunions
  const loadReunions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/reunions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReunions(data.reunions || []);
      }
    } catch (error) {
      console.error('Erreur chargement réunions:', error);
    }
  }, [token]);

  // Charger les invitations visio (depuis Gmail)
  const loadVisioInvitations = useCallback(async () => {
    setLoadingInvitations(true);
    try {
      const res = await fetch(`${API_URL}/api/visio/invitations?days_back=30`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'X-Societe-Id': currentSociete?.id || ''
        }
      });
      const data = await res.json();
      if (data.success) {
        setVisioInvitations(data.invitations || []);
      }
    } catch (error) {
      console.error('Erreur chargement invitations:', error);
    } finally {
      setLoadingInvitations(false);
    }
  }, [token, currentSociete]);

  // Charger les enregistrements visio analysés
  const loadVisioRecords = useCallback(async () => {
    setLoadingRecords(true);
    try {
      const res = await fetch(`${API_URL}/api/visio/records`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'X-Societe-Id': currentSociete?.id || ''
        }
      });
      const data = await res.json();
      setVisioRecords(data.records || []);
    } catch (error) {
      console.error('Erreur chargement records:', error);
    } finally {
      setLoadingRecords(false);
    }
  }, [token, currentSociete]);

  // Upload et analyse d'un enregistrement visio
  const handleVisioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
      toast.error('Format non supporté');
      return;
    }

    setUploadingVisio(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('titre', visioTitle || file.name);

      const res = await fetch(`${API_URL}/api/visio/analyze`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'X-Societe-Id': currentSociete?.id || ''
        },
        body: formData
      });

      const data = await res.json();
      if (data.success) {
        toast.success('Fichier envoyé !', { description: 'Analyse en cours...' });
        setVisioTitle('');
        loadVisioRecords();
      }
    } catch (error) {
      toast.error('Erreur upload');
    } finally {
      setUploadingVisio(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    loadReunions();
    loadVisioInvitations();
    loadVisioRecords();
  }, [loadReunions, loadVisioInvitations, loadVisioRecords]);

  // Scroll auto pour les messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // === ASSISTANT IA - Connecté à l'Agent Direction ===
  
  const sendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    const userMessage = { role: 'user', content: inputMessage };
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');
    setIsTyping(true);
    
    try {
      // Utiliser api (axios) - pas fetch brut qui est intercepté par le proxy
      const res = await api.post('/agents/question', {
        agent_type: 'direction',
        question: messageToSend,
        contexte_type: 'eta'
      });
      
      const data = res.data;
      const assistantMessage = {
        role: 'assistant',
        content: data.reponse || data.response || 'Je n\'ai pas compris votre demande.',
        delegueA: data.delegue_a,
        actionExecutee: data.action_executee,
        data: data.data
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (data.action_executee) {
        toast.success('Action exécutée !', {
          description: data.delegue_a ? `Via ${data.delegue_a}` : undefined
        });
      }
    } catch (error) {
      console.error('Erreur assistant:', error);
      const errMsg = error?.response?.data?.detail || error.message || 'Connexion impossible';
      toast.error('Erreur agent', { description: errMsg });
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Erreur: ${errMsg}. Rechargez la page si le probleme persiste.` 
      }]);
    } finally {
      setIsTyping(false);
    }
  };
  
  // Envoyer un message vocal transcrit à l'assistant
  const sendVoiceMessage = async (transcribedText) => {
    if (!transcribedText.trim()) return;
    
    setInputMessage('');
    const userMessage = { role: 'user', content: transcribedText, isVoice: true };
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    
    try {
      const res = await api.post('/agents/question', {
        agent_type: 'direction',
        question: transcribedText,
        contexte_type: 'eta'
      });
      
      const data = res.data;
      const assistantMessage = {
        role: 'assistant',
        content: data.reponse || 'Action traitée.',
        delegueA: data.delegue_a,
        actionExecutee: data.action_executee
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (data.action_executee) {
        toast.success('Action exécutée !');
      }
    } catch (error) {
      console.error('Erreur vocale:', error);
      toast.error('Erreur agent vocal', { description: error?.response?.data?.detail || error.message });
    } finally {
      setIsTyping(false);
    }
  };

  // === RÉUNIONS & TRANSCRIPTION ===
  
  // Démarrer l'enregistrement
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.start(1000); // Chunks de 1 seconde
      setIsRecording(true);
      setTranscription('');
      
      // Timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Envoyer des chunks toutes les 30 secondes pour transcription en temps réel
      chunkIntervalRef.current = setInterval(async () => {
        if (audioChunksRef.current.length > 0) {
          await transcribeChunk();
        }
      }, 30000);
      
      toast.success('Enregistrement démarré');
    } catch (error) {
      toast.error('Erreur', { description: 'Impossible d\'accéder au microphone' });
    }
  };

  // Arrêter l'enregistrement
  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      clearInterval(timerRef.current);
      clearInterval(chunkIntervalRef.current);
      
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      
      // Transcrire le dernier chunk
      if (audioChunksRef.current.length > 0) {
        await transcribeChunk();
      }
      
      setIsRecording(false);
      setRecordingTime(0);
      
      // Sauvegarder la réunion
      if (transcription.trim()) {
        await saveReunion();
      }
      
      toast.success('Enregistrement terminé');
    }
  };

  // Transcrire un chunk audio
  const transcribeChunk = async () => {
    if (audioChunksRef.current.length === 0) return;
    
    const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    audioChunksRef.current = [];
    
    const formData = new FormData();
    formData.append('file', blob, 'chunk.webm');
    
    try {
      setIsTranscribing(true);
      const res = await fetch(`${API_URL}/api/transcription/chunk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.transcription) {
          setTranscription(prev => prev + ' ' + data.transcription);
        }
      }
    } catch (error) {
      console.error('Erreur transcription:', error);
    } finally {
      setIsTranscribing(false);
    }
  };

  // Sauvegarder la réunion
  const saveReunion = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reunions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          titre: meetingName || `Réunion du ${new Date().toLocaleDateString('fr-FR')}`,
          transcription: transcription.trim(),
          duree_secondes: recordingTime
        })
      });
      
      if (res.ok) {
        toast.success('Réunion sauvegardée');
        loadReunions();
        setMeetingName('');
        setTranscription('');
      }
    } catch (error) {
      toast.error('Erreur sauvegarde');
    }
  };

  // Importer un fichier audio
  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('titre', file.name.replace(/\.[^/.]+$/, ''));
    
    try {
      toast.info('Import en cours...', { duration: 5000 });
      const res = await fetch(`${API_URL}/api/reunions/upload-direct`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success('Fichier importé, transcription en cours...');
        loadReunions();
      } else {
        throw new Error('Erreur import');
      }
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    }
  };

  // Analyser une réunion
  const analyzeReunion = async (reunionId) => {
    try {
      toast.info('Analyse en cours...');
      const res = await fetch(`${API_URL}/api/reunions/${reunionId}/analyser?envoyer_email=false`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Analyse terminée');
        loadReunions();
        
        // Mettre à jour la réunion sélectionnée
        if (selectedReunion?.id === reunionId) {
          setSelectedReunion(prev => ({ ...prev, analyse: data.analyse }));
        }
      }
    } catch (error) {
      toast.error('Erreur analyse');
    }
  };

  // Exécuter une action IA
  const executeAction = async (reunionId, actionIndex) => {
    try {
      const res = await fetch(`${API_URL}/api/reunions/${reunionId}/executer-action?action_index=${actionIndex}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        loadReunions();
      } else {
        throw new Error(data.detail);
      }
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    }
  };

  // Format du temps
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Crown className="w-7 h-7 text-amber-500" />
            Assistante IA
          </h1>
          <p className="text-muted-foreground">
            Votre assistant personnel et gestionnaire de réunions
          </p>
        </div>
      </div>

      {/* Onglets principaux */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="assistant" className="gap-2">
            <Bot className="w-4 h-4" /> Assistant
          </TabsTrigger>
          <TabsTrigger value="reunions" className="gap-2">
            <Mic className="w-4 h-4" /> Réunions
          </TabsTrigger>
          <TabsTrigger value="visio" className="gap-2">
            <Video className="w-4 h-4" /> Visio
          </TabsTrigger>
        </TabsList>

        {/* === ONGLET ASSISTANT === */}
        <TabsContent value="assistant" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Zone de chat */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Discussion
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4 mb-4">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                      <Bot className="w-16 h-16 mb-4 opacity-20" />
                      <p>Bonjour ! Je suis votre assistante.</p>
                      <p className="text-sm">Comment puis-je vous aider aujourd'hui ?</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, i) => (
                        <div
                          key={i}
                          className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                        >
                          {msg.role === 'assistant' && (
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <Bot className="w-4 h-4 text-primary" />
                            </div>
                          )}
                          <div
                            className={`max-w-[80%] rounded-lg px-4 py-2 ${
                              msg.role === 'user'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            }`}
                          >
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                          {msg.role === 'user' && (
                            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                              <User className="w-4 h-4 text-primary-foreground" />
                            </div>
                          )}
                        </div>
                      ))}
                      {isTyping && (
                        <div className="flex gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <Bot className="w-4 h-4 text-primary" />
                          </div>
                          <div className="bg-muted rounded-lg px-4 py-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>
                
                <div className="flex gap-2">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Posez votre question..."
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    data-testid="assistant-input"
                  />
                  <Button onClick={sendMessage} disabled={!inputMessage.trim() || isTyping}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Suggestions rapides */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Suggestions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  'Résume mes dernières interventions',
                  'Quelles tâches sont prioritaires ?',
                  'Prépare un email pour un client',
                  'Analyse mes stocks bas',
                  'Planifie ma semaine',
                ].map((suggestion, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    className="w-full justify-start text-left h-auto py-2"
                    onClick={() => {
                      setInputMessage(suggestion);
                    }}
                  >
                    {suggestion}
                  </Button>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === ONGLET RÉUNIONS === */}
        <TabsContent value="reunions" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Zone d'enregistrement */}
            <Card>
              <CardHeader>
                <CardTitle>Nouvelle réunion</CardTitle>
                <CardDescription>
                  Enregistrez vos appels et visios, obtenez une transcription automatique
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  placeholder="Nom de la réunion (optionnel)"
                  value={meetingName}
                  onChange={(e) => setMeetingName(e.target.value)}
                />
                
                {/* Bouton d'enregistrement */}
                <div className="flex flex-col items-center gap-4 py-6">
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                      isRecording
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                        : 'bg-primary hover:bg-primary/90'
                    }`}
                    data-testid="record-button"
                  >
                    {isRecording ? (
                      <Square className="w-10 h-10 text-white" />
                    ) : (
                      <Mic className="w-10 h-10 text-white" />
                    )}
                  </button>
                  
                  <div className="text-center">
                    {isRecording ? (
                      <>
                        <p className="text-lg font-mono">{formatTime(recordingTime)}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          <Waves className="w-4 h-4 animate-pulse text-red-500" />
                          Enregistrement en cours...
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Cliquez pour démarrer
                      </p>
                    )}
                  </div>
                </div>

                {/* Transcription en temps réel */}
                {(transcription || isTranscribing) && (
                  <div className="p-4 rounded-lg bg-muted">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4" />
                      <span className="font-medium">Transcription</span>
                      {isTranscribing && <Loader2 className="w-4 h-4 animate-spin" />}
                    </div>
                    <p className="text-sm">{transcription || 'En attente...'}</p>
                  </div>
                )}

                {/* Bouton import */}
                <div className="flex items-center gap-2 pt-4 border-t">
                  <span className="text-sm text-muted-foreground">ou</span>
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('file-import')?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Importer un fichier
                  </Button>
                  <input
                    id="file-import"
                    type="file"
                    accept="audio/*,video/*"
                    className="hidden"
                    onChange={handleFileImport}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Formats supportés : MP3, MP4, WebM, WAV, etc. (max 500 MB)
                </p>
              </CardContent>
            </Card>

            {/* Liste des réunions */}
            <Card>
              <CardHeader>
                <CardTitle>Mes réunions</CardTitle>
                <CardDescription>{reunions.length} réunion(s) enregistrée(s)</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {reunions.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Phone className="w-12 h-12 mx-auto mb-4 opacity-20" />
                      <p>Aucune réunion enregistrée</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reunions.map((reunion) => (
                        <Card
                          key={reunion.id}
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => setSelectedReunion(reunion)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                  {reunion.type === 'visio' ? (
                                    <Video className="w-5 h-5 text-primary" />
                                  ) : (
                                    <Phone className="w-5 h-5 text-primary" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium">{reunion.titre || 'Réunion'}</p>
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(reunion.created_at).toLocaleDateString('fr-FR')}
                                  </p>
                                </div>
                              </div>
                              <Badge variant={reunion.statut === 'transcrit' ? 'default' : 'secondary'}>
                                {reunion.statut || 'En attente'}
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === ONGLET VISIO === */}
        <TabsContent value="visio" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Invitations détectées */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Invitations Visio
                </CardTitle>
                <CardDescription>
                  Détectées depuis vos emails
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {loadingInvitations ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : visioInvitations.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Mail className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p>Aucune invitation détectée</p>
                      <p className="text-xs">Connectez votre Gmail pour voir vos invitations Zoom/Meet/Teams</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {visioInvitations.slice(0, 10).map((inv) => (
                        <div key={inv.id} className="p-3 rounded-lg border bg-card hover:bg-muted/50">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm line-clamp-1">{inv.sujet}</p>
                              <p className="text-xs text-muted-foreground">
                                {inv.plateforme} • {inv.date_reunion || 'Date ?'} {inv.heure && `à ${inv.heure}`}
                              </p>
                            </div>
                            {inv.lien_visio && (
                              <Button size="sm" variant="ghost" onClick={() => window.open(inv.lien_visio, '_blank')}>
                                Rejoindre
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
                <Button variant="outline" size="sm" className="mt-3 w-full" onClick={loadVisioInvitations}>
                  Actualiser
                </Button>
              </CardContent>
            </Card>

            {/* Upload pour analyse */}
            <Card className="border-dashed border-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Analyser un enregistrement
                </CardTitle>
                <CardDescription>
                  Upload votre visio pour transcription + résumé + actions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  placeholder="Nom de la réunion (optionnel)"
                  value={visioTitle}
                  onChange={(e) => setVisioTitle(e.target.value)}
                />
                <Button 
                  className="w-full gap-2" 
                  disabled={uploadingVisio}
                  onClick={() => document.getElementById('visio-upload-input').click()}
                >
                  {uploadingVisio ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours...</>
                  ) : (
                    <><Upload className="w-4 h-4" /> Sélectionner un fichier</>
                  )}
                </Button>
                <input
                  type="file"
                  id="visio-upload-input"
                  className="hidden"
                  accept="video/*,audio/*"
                  onChange={handleVisioUpload}
                />
                <p className="text-xs text-muted-foreground text-center">
                  MP4, WebM, MP3, WAV (max 500 MB)
                </p>
              </CardContent>
            </Card>

            {/* Liste des analyses */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Analyses récentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[250px]">
                  {loadingRecords ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : visioRecords.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Video className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p>Aucune analyse</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {visioRecords.map((rec) => (
                        <div key={rec.id} className="p-3 rounded-lg border bg-card flex items-center justify-between">
                          <div>
                            <p className="font-medium">{rec.titre}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(rec.created_at).toLocaleDateString('fr-FR')} • 
                              {rec.transcription?.status === 'completed' ? ' Transcrit' : ' En cours...'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {rec.transcription?.status === 'completed' && (
                              <Badge variant="default"><CheckCircle2 className="w-3 h-3 mr-1" />OK</Badge>
                            )}
                            {rec.transcription?.status === 'processing' && (
                              <Badge variant="secondary"><Loader2 className="w-3 h-3 mr-1 animate-spin" />...</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal détail réunion */}
      <Dialog open={!!selectedReunion} onOpenChange={() => setSelectedReunion(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedReunion?.titre || 'Réunion'}</DialogTitle>
            <DialogDescription>
              {selectedReunion?.created_at && new Date(selectedReunion.created_at).toLocaleString('fr-FR')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedReunion && (
            <Tabs defaultValue="transcription" className="flex-1 overflow-hidden">
              <TabsList>
                <TabsTrigger value="transcription">Transcription</TabsTrigger>
                <TabsTrigger value="analyse" disabled={!selectedReunion.analyse}>
                  Analyse IA
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="transcription" className="mt-4">
                <ScrollArea className="h-[50vh]">
                  <div className="pr-4">
                    {selectedReunion.transcription ? (
                      <p className="whitespace-pre-wrap">{selectedReunion.transcription}</p>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">
                        Transcription en cours ou non disponible
                      </p>
                    )}
                  </div>
                </ScrollArea>
                
                {selectedReunion.transcription && !selectedReunion.analyse && (
                  <div className="mt-4 pt-4 border-t">
                    <Button onClick={() => analyzeReunion(selectedReunion.id)}>
                      <Brain className="w-4 h-4 mr-2" />
                      Analyser avec l'IA
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="analyse" className="mt-4">
                {selectedReunion.analyse?.json && (
                  <ScrollArea className="h-[50vh]">
                    <div className="space-y-6 pr-4">
                      {/* Résumé */}
                      <div>
                        <h3 className="font-semibold mb-2">Résumé</h3>
                        <p className="text-sm text-muted-foreground">
                          {selectedReunion.analyse.json.resume}
                        </p>
                      </div>
                      
                      {/* Points clés */}
                      {selectedReunion.analyse.json.points_cles?.length > 0 && (
                        <div>
                          <h3 className="font-semibold mb-2">Points clés</h3>
                          <ul className="space-y-1">
                            {selectedReunion.analyse.json.points_cles.map((p, i) => (
                              <li key={i} className="text-sm flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                                {p}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Actions IA */}
                      {selectedReunion.analyse.json.actions_ia_proposees?.length > 0 && (
                        <div className="border-t pt-4">
                          <h3 className="font-semibold mb-2 flex items-center gap-2">
                            <Brain className="w-4 h-4 text-purple-500" />
                            Actions proposées par l'IA
                          </h3>
                          <div className="space-y-2">
                            {selectedReunion.analyse.json.actions_ia_proposees.map((action, i) => (
                              <Button
                                key={i}
                                variant={action.executed ? 'secondary' : 'outline'}
                                className="w-full justify-start h-auto py-3"
                                disabled={action.executed}
                                onClick={() => executeAction(selectedReunion.id, i)}
                              >
                                <div className="flex items-center gap-3">
                                  {action.type === 'intervention' && <Tractor className="w-4 h-4 text-green-500" />}
                                  {action.type === 'email' && <Mail className="w-4 h-4 text-blue-500" />}
                                  {action.type === 'rdv' && <Calendar className="w-4 h-4 text-orange-500" />}
                                  {action.type === 'tache' && <ListTodo className="w-4 h-4 text-purple-500" />}
                                  <div className="text-left">
                                    <p className="text-sm font-medium">{action.description}</p>
                                    {action.executed && (
                                      <p className="text-xs text-green-500">✓ Exécuté</p>
                                    )}
                                  </div>
                                </div>
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
